import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, LogOut, Trash2, Plus, Users, Briefcase, FileText, Mail, Phone, Download, UploadCloud, X, Film } from "lucide-react";

const VIDEO_EXTS = ["mp4", "webm", "mov", "m4v", "mkv", "avi", "ogv", "qt", "3gp"];
const IMAGE_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "avif", "bmp", "svg", "heic", "heif"];

function UploadDropzone({ value, onChange, accept = "image/*", label = "Upload file", testid }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const isVideo = accept.includes("video");

  const doUpload = async (file) => {
    if (!file) return;
    const ext = (file.name || "").split(".").pop().toLowerCase();
    const mime = (file.type || "").toLowerCase();
    const looksVideo = mime.startsWith("video/") || VIDEO_EXTS.includes(ext);
    const looksImage = mime.startsWith("image/") || IMAGE_EXTS.includes(ext);
    if (isVideo && !looksVideo) { toast.error("Please choose a video file (mp4, webm, mov)"); return; }
    if (!isVideo && !looksImage) { toast.error("Please choose an image file (jpg, png, webp)"); return; }
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success(`${isVideo ? "Video" : "Image"} uploaded!`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed. Please try a smaller file or check your connection.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div>
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}
        data-testid={testid}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed p-4 text-center transition-colors ${drag ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-white/15 bg-white/[0.03] hover:border-[#00E5FF]/50"}`}
      >
        {uploading ? (
          <div className="py-4 text-sm text-[#00E5FF]">
            Uploading… {progress > 0 ? `${progress}%` : ""}
            <div className="mx-auto mt-2 h-1.5 w-40 overflow-hidden rounded-full bg-white/10">
              <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : value ? (
          <div className="flex items-center gap-3">
            {isVideo ? (
              <span className="grid h-12 w-16 place-items-center rounded-lg bg-black/40"><Film className="h-5 w-5 text-[#00FFB2]" /></span>
            ) : (
              <img src={value} alt="preview" className="h-12 w-16 rounded-lg object-cover" />
            )}
            <span className="flex-1 truncate text-xs text-white/60">{isVideo ? "Video attached" : "Image attached"} · click to replace</span>
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }} data-testid={`${testid}-remove`} className="grid h-7 w-7 place-items-center rounded-full bg-white/5 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="py-3">
            <UploadCloud className="mx-auto h-6 w-6 text-white/40" />
            <div className="mt-1 text-xs text-white/60">{label}</div>
            <div className="text-[10px] text-white/30">{isVideo ? "MP4/WebM up to 200MB" : "JPG/PNG/WebP up to 12MB"} · drag & drop or click</div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept={isVideo ? "video/*,.mp4,.mov,.webm,.mkv,.m4v,.avi,.3gp,.ogv" : "image/*,.jpg,.jpeg,.png,.webp,.gif,.avif,.heic,.heif"} data-testid={testid ? `${testid}-input` : undefined} className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
    </div>
  );
}

function exportLeadsCSV(leads) {
  const headers = ["name", "email", "phone", "company", "source", "requirement", "status", "created_at", "meta"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = leads.map((l) => headers.map((h) => esc(h === "meta" ? JSON.stringify(l.meta || {}) : l[h])).join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `9nocai-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const { user, ready, login, logout } = useAuth();
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Admin · 9nocAI CMS";
    return () => {
      if (meta.parentNode) document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);
  if (!ready) return <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white/60">Loading...</div>;
  if (!user) return <LoginScreen onLogin={login} />;
  return <Dashboard onLogout={logout} />;
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const res = await onLogin(email, password);
    setLoading(false);
    if (!res.ok) setErr(res.error);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white px-6 grid-bg">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl glass-strong p-8" data-testid="admin-login">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7B61FF]"><Sparkles className="h-4 w-4 text-[#0B1220]" /></span>
          <span className="font-display text-lg font-bold">9nocAI <span className="text-white/40 text-sm">CMS</span></span>
        </div>
        <h1 className="font-display text-2xl font-bold">Admin Login</h1>
        <p className="text-sm text-white/50 mt-1">Manage portfolio, leads and blog.</p>
        <div className="mt-6 space-y-3">
          <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
          <Input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF]" />
        </div>
        {err && <p className="mt-3 text-sm text-red-400" data-testid="admin-error">{err}</p>}
        <button disabled={loading} data-testid="admin-login-btn" className="mt-6 w-full rounded-full bg-gradient-to-r from-[#00E5FF] to-[#7B61FF] py-3 font-semibold text-[#0B1220] disabled:opacity-50">
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" className="mt-4 block text-center text-xs text-white/40 hover:text-white/70">← Back to website</a>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [blog, setBlog] = useState([]);

  const loadAll = () => {
    api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
    api.get("/blog").then(({ data }) => setBlog(data)).catch(() => {});
  };
  useEffect(loadAll, []);

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      <div className="border-b border-white/10 glass-strong sticky top-0 z-20">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7B61FF]"><Sparkles className="h-4 w-4 text-[#0B1220]" /></span>
            <span className="font-display font-bold">9nocAI <span className="text-white/40 text-sm">CMS</span></span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-white/60 hover:text-white">View Site</a>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm hover:border-red-400/50"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Total Leads" value={leads.length} />
          <StatCard icon={Briefcase} label="Portfolio Items" value={portfolio.length} />
          <StatCard icon={FileText} label="Blog Posts" value={blog.length} />
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <LeadsTable leads={leads} reload={loadAll} />
          </TabsContent>
          <TabsContent value="portfolio" className="mt-6">
            <PortfolioManager items={portfolio} reload={loadAll} />
          </TabsContent>
          <TabsContent value="blog" className="mt-6">
            <BlogManager items={blog} reload={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl glass p-5 flex items-center gap-4">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/5"><Icon className="h-5 w-5 text-[#00E5FF]" /></span>
    <div><div className="font-display text-2xl font-bold">{value}</div><div className="text-xs text-white/50">{label}</div></div>
  </div>
);

function LeadsTable({ leads, reload }) {
  const del = async (id) => { await api.delete(`/leads/${id}`); toast.success("Lead deleted"); reload(); };
  if (!leads.length) return <Empty text="No leads yet. They'll appear here as visitors submit forms." />;
  return (
    <div className="space-y-3" data-testid="leads-list">
      <div className="flex items-center justify-between">
        <span className="text-sm text-white/50">{leads.length} lead{leads.length !== 1 ? "s" : ""}</span>
        <button onClick={() => exportLeadsCSV(leads)} data-testid="leads-export-csv"
          className="inline-flex items-center gap-2 rounded-full bg-[#00FFB2] text-[#0B1220] px-4 py-2 text-sm font-semibold hover:scale-[1.02] transition-transform">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      {leads.map((l) => (
        <div key={l.id} className="rounded-2xl glass p-5" data-testid={`lead-${l.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{l.name}</span>
                <span className="rounded-full bg-[#00E5FF]/10 px-2.5 py-0.5 text-xs text-[#00E5FF]">{l.source}</span>
                {l.company && <span className="text-xs text-white/40">· {l.company}</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/60">
                <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {l.email}</span>
                {l.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {l.phone}</span>}
              </div>
              {l.requirement && <p className="mt-2 text-sm text-white/50">{l.requirement}</p>}
              {l.meta && Object.keys(l.meta).length > 0 && (
                <p className="mt-2 text-xs text-white/30">{JSON.stringify(l.meta)}</p>
              )}
            </div>
            <button onClick={() => del(l.id)} data-testid={`lead-delete-${l.id}`} className="grid h-9 w-9 place-items-center rounded-full glass hover:border-red-400/50 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PortfolioManager({ items, reload }) {
  const empty = { name: "", category: "Websites", industry: "", problem: "", solution: "", result: "", image: "", video: "", tags: "" };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      const tags = form.tags ? form.tags.split(/[,|]/).map((t) => t.trim()).filter(Boolean) : [];
      await api.post("/portfolio", { ...form, tags });
      toast.success("Portfolio item added"); setForm(empty); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/portfolio/${id}`); toast.success("Deleted"); reload(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl glass p-6 space-y-3" data-testid="portfolio-add-form">
        <h3 className="font-display font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-[#00FFB2]" /> Add Portfolio Item</h3>
        <Input placeholder="Project name" value={form.name} onChange={set("name")} data-testid="pf-name" className="bg-white/5 border-white/10" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Category" value={form.category} onChange={set("category")} data-testid="pf-category" className="bg-white/5 border-white/10" />
          <Input placeholder="Industry" value={form.industry} onChange={set("industry")} className="bg-white/5 border-white/10" />
        </div>
        <Textarea placeholder="Problem" value={form.problem} onChange={set("problem")} rows={2} className="bg-white/5 border-white/10 resize-none" />
        <Textarea placeholder="Solution" value={form.solution} onChange={set("solution")} rows={2} className="bg-white/5 border-white/10 resize-none" />
        <Input placeholder="Result" value={form.result} onChange={set("result")} className="bg-white/5 border-white/10" />
        <div className="space-y-1">
          <label className="text-xs text-white/50">Cover image</label>
          <UploadDropzone value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} accept="image/*" label="Upload cover image" testid="pf-image-upload" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-white/50">Promotional / sample video (optional)</label>
          <UploadDropzone value={form.video} onChange={(url) => setForm((f) => ({ ...f, video: url }))} accept="video/*" label="Upload video from your computer" testid="pf-video-upload" />
        </div>
        <Input placeholder="Tags (comma or | separated)" value={form.tags} onChange={set("tags")} className="bg-white/5 border-white/10" />
        <button onClick={add} data-testid="pf-add-btn" className="w-full rounded-full bg-[#00FFB2] text-[#0B1220] py-2.5 font-semibold">Add Item</button>
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="rounded-2xl glass p-4 flex items-center gap-4" data-testid={`pf-item-${p.id}`}>
            {p.image && <img src={p.image} alt="" className="h-14 w-14 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate flex items-center gap-2">{p.name}{p.video && <Film className="h-3.5 w-3.5 text-[#00FFB2] shrink-0" />}</div>
              <div className="text-xs text-white/40">{p.category} · {p.industry}</div>
            </div>
            <button onClick={() => del(p.id)} data-testid={`pf-delete-${p.id}`} className="grid h-9 w-9 place-items-center rounded-full glass hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogManager({ items, reload }) {
  const empty = { title: "", excerpt: "", content: "", tag: "AI", image: "", author: "9nocAI Team" };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const add = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try { await api.post("/blog", form); toast.success("Post added"); setForm(empty); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/blog/${id}`); toast.success("Deleted"); reload(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl glass p-6 space-y-3" data-testid="blog-add-form">
        <h3 className="font-display font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-[#00FFB2]" /> Add Blog Post</h3>
        <Input placeholder="Title" value={form.title} onChange={set("title")} data-testid="blog-title" className="bg-white/5 border-white/10" />
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Tag" value={form.tag} onChange={set("tag")} className="bg-white/5 border-white/10" />
          <Input placeholder="Author" value={form.author} onChange={set("author")} className="bg-white/5 border-white/10" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-white/50">Cover image</label>
          <UploadDropzone value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} accept="image/*" label="Upload cover image" testid="blog-image-upload" />
        </div>
        <Textarea placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} rows={2} className="bg-white/5 border-white/10 resize-none" />
        <Textarea placeholder="Content" value={form.content} onChange={set("content")} rows={4} className="bg-white/5 border-white/10 resize-none" />
        <button onClick={add} data-testid="blog-add-btn" className="w-full rounded-full bg-[#00FFB2] text-[#0B1220] py-2.5 font-semibold">Add Post</button>
      </div>
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} className="rounded-2xl glass p-4 flex items-center gap-4" data-testid={`blog-item-${b.id}`}>
            {b.image && <img src={b.image} alt="" className="h-14 w-14 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{b.title}</div><div className="text-xs text-white/40">{b.tag}</div></div>
            <button onClick={() => del(b.id)} data-testid={`blog-delete-${b.id}`} className="grid h-9 w-9 place-items-center rounded-full glass hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl glass p-12 text-center text-white/40">{text}</div>;
